import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Space } from 'antd';
import React, { useEffect, useState } from 'react';
import { useReceivingReportStore } from '../../../../../store/ReceivingReportStore';
import {
  useFetchData,
  useFetchFile,
  useUpdateData,
} from '../../../../../hooks';
import { ReceivingReportRecord } from '../../../../../types/ReceivingReport';
import { RRS_API } from '../../../../../hooks/useReceivingReportHooks';
import {
  useDeleteMRFile,
  useUploadMRFiles,
} from '../../../../../hooks/useMaterialRequestHooks';
import { BaseModel, FormField, PageableResponse } from '../../../../../types';
import { Currency, Resource, RRPurposeEnum } from '../../../../../lib/enums';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { dateFormat } from '../../../../../lib/utils';
import PageHeaderAction from '../../../../../components/PageHeaderAction';
import SubmitButton from '../../../../../components/ui/SubmitButton';
import CustomForm from '../../../../../components/ui/CustomForm';
import ErrorModal from '../../../../../components/ui/modal/ErrorModal';
import InfoModal from '../../../../../components/ui/modal/InfoModal';
import ReceivingReportInfoLoadingView from '../../info/ReceivingReportInfoLoadingView';
import ReceivingReportMovementUpdate from './ReceivingReportMovementUpdate';

const ReceivingReportEditForm = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const receivingReportStore = useReceivingReportStore();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState<string | undefined>('');

  const { isLoading, data, isError, error } =
    useFetchData<ReceivingReportRecord>(`${RRS_API}/${params.id}`);

  const {
    mutate: mutateFile,
    isError: isFileError,
    error: fileError,
  } = useUploadMRFiles();
  const { data: dataFiles } = useFetchFile(
    `${RRS_API}/${params.id}/files`,
    'api/stock/rrs',
  );
  const mutationDelete = useDeleteMRFile();
  const {
    mutate: mutate,
    isSuccess: isSuccessUpdate,
    isError: isErrorUpdate,
    error: errorUpadte,
    isPending,
  } = useUpdateData(`${RRS_API}`);

  const rrPurposeOptions: BaseModel[] = Object.keys(RRPurposeEnum).map(
    (key) =>
      ({
        id: key,
        code: RRPurposeEnum[key as keyof typeof RRPurposeEnum],
        description: RRPurposeEnum[key as keyof typeof RRPurposeEnum],
      }) as BaseModel,
  );

  const currencyOptions: BaseModel[] = Object.keys(Currency).map(
    (key) =>
      ({
        id: key,
        code: Currency[key as keyof typeof Currency],
        description: Currency[key as keyof typeof Currency],
      }) as BaseModel,
  );

  const { data: defaultLocationData, isLoading: isDefaultLocationLoading } =
    useFetchData<PageableResponse<BaseModel>>('/location/locations', {
      resource: Resource.MIN,
      search: '',
      length: '999999',
    });

  const { data: vendorsData, isLoading: isVendorLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('/partner/vendors', {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    navigate('/stock-movement/receiving-report');
  };

  const handleClose = () => {
    navigate('/stock-movement/receiving-report');
  };

  useEffect(() => {
    if (data !== undefined) {
      data.movements.map((movement) => {
        movement.uuid = uuidv4();
      });
      receivingReportStore.setFormData(data);
    }
    if (isError) {
      setIsErrorModalOpen(true);
      setErrorMess(error.message);
    }
    if (isFileError) {
      setIsErrorModalOpen(true);
      setErrorMess(fileError.message);
    }
  }, [data, isError]);

  useEffect(() => {
    if (isSuccessUpdate) setIsInfoModalOpen(true);
    if (isErrorUpdate) {
      setIsErrorModalOpen(true);
      setErrorMess(errorUpadte?.message);
    }
  }, [isSuccessUpdate, isErrorUpdate]);

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  const handleDeleteFile = (file: any) => {
    mutationDelete.mutate(file.uid);
  };

  const onFinish = (values: any) => {
    const destination: BaseModel | undefined = defaultLocationData?.data.find(
      (obj) => {
        return obj.id == values.destination;
      },
    );

    const vendor: BaseModel | undefined = vendorsData?.data.find((obj) => {
      return obj.id == values.vendor;
    });

    const payload: ReceivingReportRecord = {
      ...values,
      id: params.id,
      code: data?.code,
      destination: destination,
      requiredDate:
        values.requiredDate === null
          ? null
          : dayjs(values.requiredDate).format(dateFormat),
      receivedDate:
        values.receivedDate === null
          ? null
          : dayjs(values.receivedDate).format(dateFormat),
      source: values.source,
      proderNo: values.proderNo,
      type: values.type,
      notes: values.notes,
      vendor: vendor,
      wtax: values.wtax,
      discount: values.discount,
      sku: values.sku,
      status: values.status,
      subTotal: values.subTotal,
      rrPurpose: values.rrPurpose,
      total: values.total,
      drNo: values.drNo,
      currency: values.currency,
      vat: values.vat,
      invoiceNo: values.invoiceNo,
      movements: receivingReportStore.formData.movements || [],
    };
    mutate({
      id: params.id,
      form: payload,
    });
    if (values.files !== undefined) {
      const fileList = values.files.fileList;
      const formData = new FormData();
      fileList.forEach((file: any) => {
        formData.append('files', file.originFileObj);
      });
      mutateFile({ id: params.id, formData: formData });
    }
  };

  const fileUploads = dataFiles?.map((file) => ({
    uid: file.id,
    name: file.origFilename,
    status: 'done',
    url: file.code,
  }));

  const fields: FormField[] = [
    {
      name: 'code',
      label: 'Code',
      type: 'input',
      disabled: true,
      defaultValue: data?.code,
    },
    {
      name: 'requiredDate',
      label: 'Required Date',
      rules: [{ required: true, message: 'Please select required date' }],
      type: 'datepicker',
      defaultValue: data?.requiredDate,
    },
    {
      name: 'receivedDate',
      label: 'Received Date',
      type: 'datepicker',
      defaultValue: data?.receivedDate,
    },
    {
      name: 'destination',
      label: 'Destination',
      allowClear: true,
      type: 'select',
      isLoading: isDefaultLocationLoading,
      isMultiple: false,
      values: defaultLocationData?.data,
      rules: [{ required: true, message: 'Please select destination' }],
      defaultValue: data?.destination?.id,
    },
    {
      name: 'vendor',
      label: 'Vendor',
      allowClear: true,
      type: 'select',
      isLoading: isVendorLoading,
      isMultiple: false,
      values: vendorsData?.data,
      defaultValue: data?.vendor?.id,
    },
    {
      name: 'rrPurpose',
      label: 'RR Purpose',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      values: rrPurposeOptions,
      defaultValue: data?.rrPurpose,
    },
    {
      name: 'invoiceNo',
      label: 'Invoice No',
      type: 'input',
      defaultValue: data?.invoiceNo,
    },
    {
      name: 'drNo',
      label: 'DR No',
      type: 'input',
      defaultValue: data?.drNo,
    },
    {
      name: 'source',
      label: 'Source Document',
      type: 'input',
      defaultValue: data?.source,
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'radio-group-button',
      defaultValue: data?.currency,
      values: currencyOptions,
    },
    {
      name: 'wtax',
      label: 'Withholding Tax',
      type: 'input',
      defaultValue: data?.wtax,
    },
    {
      name: 'discount',
      label: 'Discount',
      type: 'input',
      defaultValue: data?.discount,
    },
    {
      name: 'vat',
      label: 'VAT',
      type: 'input',
      defaultValue: data?.vat,
    },
    {
      name: 'notes',
      label: 'Remarks',
      type: 'textarea',
      defaultValue: data?.source,
    },
    {
      name: 'status',
      label: 'Status',
      type: 'status',
      defaultValue: data?.status,
    },
    {
      name: 'files',
      label: 'Attachments',
      disabled: false,
      type: 'file',
      defaultValue: fileUploads,
    },
  ];

  return (
    <>
      <div className={'flex items-center justify-between'}>
        <PageHeaderAction action={'Request'} moduleName={'Receiving Report'} />
        <Space>
          <Button onClick={handleClose}>Close</Button>
          <SubmitButton loading={isPending} form={form}>
            Submit
          </SubmitButton>
        </Space>
      </div>
      <div className="bg-white mt-4 h-[calc(84vh)] overflow-auto rounded-xl">
        {isLoading ? (
          <div className="p-4">
            <ReceivingReportInfoLoadingView active={true} />
            <div className={'text-base font-semibold leading-6'}>
              Order Lines
            </div>
          </div>
        ) : (
          <div>
            <div className="p-4">
              <CustomForm
                onDeleteFile={handleDeleteFile}
                data={{ ...data, files: fileUploads }}
                formFields={fields}
                form={form}
                onFinish={onFinish}
                rowCol={3}
                layout={'vertical'}
                formName={'po_form_update'}
              />
            </div>
            <ReceivingReportMovementUpdate />
          </div>
        )}
      </div>

      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <InfoModal
        visible={isInfoModalOpen}
        message={'Receiving Report successfully updated'}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default ReceivingReportEditForm;
